import { blobticMessage } from '../../Commun/components/BlobticMessage';
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, IconButton, Grid, Chip, TextField, LinearProgress, Stack, Alert, Switch,
  FormControlLabel, InputAdornment, Divider, CircularProgress, Typography, Tooltip, MenuItem
} from '@mui/material';
import {
  Save as SaveIcon,
  Timer as TimerIcon,
  Quiz as QuizIcon,
  People as PeopleIcon,
  FactCheck as FactCheckIcon,
  Person as PersonIcon,
  AccessTime as AccessTimeIcon,
  AssignmentInd as AssignmentIndIcon,
  ReceiptLong as ReceiptLongIcon,
  Description as InvoiceIcon,
  RestartAlt as ResetIcon,
  DeleteForever as DeleteForeverIcon
} from '@mui/icons-material';
import GenericPage from '../../Commun/components/GenericPage';
import { formatDocumentNumber, academyStore } from '../services/academyStore';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import { useAuth } from '../../Commun/contexts/AuthContext';

export const AcademyParametersPage: React.FC = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  

  // Connected Agency ID (Safe parsing to prevent NaN)
  const rawAgency = user?.agency;
  const parsedDosId = rawAgency ? parseInt(String(rawAgency), 10) : NaN;
  const connectedDosId = (!isNaN(parsedDosId) && parsedDosId > 0) ? parsedDosId : 1;

  const [loading, setLoading] = useState<boolean>(true);
  const [savingCode, setSavingCode] = useState<string | null>(null);
  const [, setTick] = useState(0);

  // Form Field States
  const [timerSeconds, setTimerSeconds] = useState<number>(60);
  const [autoApprove, setAutoApprove] = useState<boolean>(false);
  const [quizMinScore, setQuizMinScore] = useState<number>(60);
  const [maxClassStudents, setMaxClassStudents] = useState<number>(25);
  const [showUserOnEditions, setShowUserOnEditions] = useState<boolean>(true);
  const [deleteGroupCascadeSessions, setDeleteGroupCascadeSessions] = useState<boolean>(true);

  // Invoice Numbering Form States
  const [invPrefix, setInvPrefix] = useState<string>('INV');
  const [invAgencyLength, setInvAgencyLength] = useState<number>(4);
  const [invDateFormat, setInvDateFormat] = useState<string>('YYYY');
  const [invPadding, setInvPadding] = useState<number>(4);
  const [invSuffix, setInvSuffix] = useState<string>('');
  const [invCounter, setInvCounter] = useState<number>(1);
  const [invResetPeriod, setInvResetPeriod] = useState<string>('Yearly');
  const [invOrder, setInvOrder] = useState<string>('PREFIX,AGENCY,DATE,NUMBER,SUFFIX');
  const [invSeparator, setInvSeparator] = useState<string>('-');

  // Payment Numbering Form States
  const [payPrefix, setPayPrefix] = useState<string>('REC');
  const [payAgencyLength, setPayAgencyLength] = useState<number>(4);
  const [payDateFormat, setPayDateFormat] = useState<string>('None');
  const [payPadding, setPayPadding] = useState<number>(4);
  const [paySuffix, setPaySuffix] = useState<string>('');
  const [payCounter, setPayCounter] = useState<number>(1);
  const [payResetPeriod, setPayResetPeriod] = useState<string>('Never');
  const [payOrder, setPayOrder] = useState<string>('PREFIX,NUMBER,SUFFIX');
  const [paySeparator, setPaySeparator] = useState<string>('-');

  // Planning Configuration Parameters
  const [planningStartTime, setPlanningStartTime] = useState<string>('08:00');
  const [planningEndTime, setPlanningEndTime] = useState<string>('17:30');
  const [planningSlotDuration, setPlanningSlotDuration] = useState<number>(90);
  const [planningFirstDayOfWeek, setPlanningFirstDayOfWeek] = useState<number>(0);
  const [planningDefaultView, setPlanningDefaultView] = useState<'day' | 'week' | 'month'>('week');
  const [planningWeekendDays, setPlanningWeekendDays] = useState<number[]>([5, 6]);
  const [planningWeekendBgColor, setPlanningWeekendBgColor] = useState<string>('#fff7ed');

  // Subscribe to store updates
  useEffect(() => {
    return academyStore.subscribe(() => {
      setTick(t => t + 1);
    });
  }, []);

  // Update local parameter form values whenever store changes or agency ID resolves
  useEffect(() => {
    if (!connectedDosId) return;
    const tVal = academyStore.getAcademyParameterValue('Dashboard_Refresh_Timer_Seconds', connectedDosId, '60');
    const aVal = academyStore.getAcademyParameterValue('Auto_Approve_PreRegistrations', connectedDosId, '0');
    const qVal = academyStore.getAcademyParameterValue('Default_Quiz_Passing_Score', connectedDosId, '60');
    const mVal = academyStore.getAcademyParameterValue('Max_Active_Students_Per_Class', connectedDosId, '25');
    const sUserVal = academyStore.getAcademyParameterValue('Show_User_On_Editions', connectedDosId, '1');
    const cascadeVal = academyStore.getAcademyParameterValue('Delete_Group_Cascade_Sessions', connectedDosId, '1');

    const pStart = academyStore.getPlanningStartTime(connectedDosId);
    const pEnd = academyStore.getPlanningEndTime(connectedDosId);
    const pDur = academyStore.getPlanningSlotDurationMinutes(connectedDosId);
    const pDay = academyStore.getPlanningFirstDayOfWeek(connectedDosId);
    const pView = academyStore.getPlanningDefaultView(connectedDosId);
    const pWDays = academyStore.getPlanningWeekendDays(connectedDosId);
    const pWColor = academyStore.getPlanningWeekendBgColor(connectedDosId);

    setTimerSeconds(parseInt(tVal, 10) || 0);
    setAutoApprove(aVal === '1');
    setQuizMinScore(parseInt(qVal, 10) || 60);
    setMaxClassStudents(parseInt(mVal, 10) || 25);
    setShowUserOnEditions(sUserVal === '1');
    setDeleteGroupCascadeSessions(cascadeVal === '1' || cascadeVal.toLowerCase() === 'true');

    setPlanningStartTime(pStart);
    setPlanningEndTime(pEnd);
    setPlanningSlotDuration(pDur);
    setPlanningFirstDayOfWeek(pDay);
    setPlanningDefaultView(pView);
    setPlanningWeekendDays(pWDays);
    setPlanningWeekendBgColor(pWColor);

    // Invoice parameters
    setInvPrefix(academyStore.getAcademyParameterValue('Invoice_Number_Prefix', connectedDosId, 'INV'));
    setInvAgencyLength(parseInt(academyStore.getAcademyParameterValue('Invoice_Number_Agency_Length', connectedDosId, '4'), 10) || 0);
    setInvDateFormat(academyStore.getAcademyParameterValue('Invoice_Number_DateFormat', connectedDosId, 'YYYY'));
    setInvPadding(parseInt(academyStore.getAcademyParameterValue('Invoice_Number_Padding', connectedDosId, '4'), 10) || 4);
    setInvSuffix(academyStore.getAcademyParameterValue('Invoice_Number_Suffix', connectedDosId, ''));
    setInvCounter(parseInt(academyStore.getAcademyParameterValue('Invoice_Number_Next_Counter', connectedDosId, '1'), 10) || 1);
    setInvResetPeriod(academyStore.getAcademyParameterValue('Invoice_Number_Reset_Period', connectedDosId, 'Yearly'));
    setInvOrder(academyStore.getAcademyParameterValue('Invoice_Number_Order', connectedDosId, 'PREFIX,AGENCY,DATE,NUMBER,SUFFIX'));
    setInvSeparator(academyStore.getAcademyParameterValue('Invoice_Number_Separator', connectedDosId, '-'));

    // Payment parameters
    setPayPrefix(academyStore.getAcademyParameterValue('Payment_Number_Prefix', connectedDosId, 'REC'));
    setPayAgencyLength(parseInt(academyStore.getAcademyParameterValue('Payment_Number_Agency_Length', connectedDosId, '4'), 10) || 0);
    setPayDateFormat(academyStore.getAcademyParameterValue('Payment_Number_DateFormat', connectedDosId, 'None'));
    setPayPadding(parseInt(academyStore.getAcademyParameterValue('Payment_Number_Padding', connectedDosId, '4'), 10) || 4);
    setPaySuffix(academyStore.getAcademyParameterValue('Payment_Number_Suffix', connectedDosId, ''));
    setPayCounter(parseInt(academyStore.getAcademyParameterValue('Payment_Number_Next_Counter', connectedDosId, '1'), 10) || 1);
    setPayResetPeriod(academyStore.getAcademyParameterValue('Payment_Number_Reset_Period', connectedDosId, 'Never'));
    setPayOrder(academyStore.getAcademyParameterValue('Payment_Number_Order', connectedDosId, 'PREFIX,NUMBER,SUFFIX'));
    setPaySeparator(academyStore.getAcademyParameterValue('Payment_Number_Separator', connectedDosId, '-'));
  }, [connectedDosId]);

  // Save single parameter handler
  const handleSaveSingleParameter = async (code: string, value: string, lib: string, type: string = 'N') => {
    setSavingCode(code);
    try {
      const existingParam = academyStore.getAcademyParameters(connectedDosId).find(p => p.code === code);
      const res = await academyStore.saveAcademyParameter({
        id: existingParam?.id,
        dos_idf: connectedDosId,
        code,
        value,
        lib,
        type
      });

      if (res && res.success) {
        blobticMessage.success(
          t('academy.parameters.saveTooltip'),
          t('academy.parameters.saveSuccess', code)
        );
        setTick(t => t + 1);
      } else {
        blobticMessage.error(
          t('academy.parameters.saveTooltip'),
          res?.message || t('academy.parameters.saveError', code)
        );
      }
    } catch (err) {
      console.error(`Failed to save parameter ${code}:`, err);
      blobticMessage.error(
        t('academy.parameters.saveTooltip'),
        t('academy.parameters.saveError', code)
      );
    } finally {
      setSavingCode(null);
    }
  };

  // Helper: resolve existing parameter id by code to ensure Update (not Insert) is sent
  const getParamId = (code: string): number | undefined =>
    academyStore.getAcademyParameters(connectedDosId).find(p => p.code === code)?.id;

  const handleSaveInvoiceNumbering = async () => {
    setSavingCode('Invoice_Numbering');
    try {
      await academyStore.saveAcademyParameter({ id: getParamId('Invoice_Number_Prefix'),        dos_idf: connectedDosId, code: 'Invoice_Number_Prefix',        value: invPrefix,             lib: 'Préfixe facture',           type: 'S' });
      await academyStore.saveAcademyParameter({ id: getParamId('Invoice_Number_Agency_Length'), dos_idf: connectedDosId, code: 'Invoice_Number_Agency_Length', value: String(invAgencyLength), lib: 'Longueur agence facture',    type: 'N' });
      await academyStore.saveAcademyParameter({ id: getParamId('Invoice_Number_DateFormat'),    dos_idf: connectedDosId, code: 'Invoice_Number_DateFormat',    value: invDateFormat,          lib: 'Format date facture',       type: 'S' });
      await academyStore.saveAcademyParameter({ id: getParamId('Invoice_Number_Padding'),       dos_idf: connectedDosId, code: 'Invoice_Number_Padding',       value: String(invPadding),     lib: 'Longueur zéros facture',   type: 'N' });
      await academyStore.saveAcademyParameter({ id: getParamId('Invoice_Number_Suffix'),        dos_idf: connectedDosId, code: 'Invoice_Number_Suffix',        value: invSuffix,             lib: 'Suffixe facture',           type: 'S' });
      await academyStore.saveAcademyParameter({ id: getParamId('Invoice_Number_Next_Counter'),  dos_idf: connectedDosId, code: 'Invoice_Number_Next_Counter',  value: String(invCounter),     lib: 'Compteur facture',          type: 'N' });
      await academyStore.saveAcademyParameter({ id: getParamId('Invoice_Number_Reset_Period'),  dos_idf: connectedDosId, code: 'Invoice_Number_Reset_Period',  value: invResetPeriod,        lib: 'Remise à zéro facture',    type: 'S' });
      await academyStore.saveAcademyParameter({ id: getParamId('Invoice_Number_Order'),         dos_idf: connectedDosId, code: 'Invoice_Number_Order',         value: invOrder,               lib: 'Ordre éléments facture',   type: 'S' });
      await academyStore.saveAcademyParameter({ id: getParamId('Invoice_Number_Separator'),     dos_idf: connectedDosId, code: 'Invoice_Number_Separator',     value: invSeparator,          lib: 'Séparateur facture',        type: 'S' });

      blobticMessage.success(t('academy.parameters.saveTooltip'), t('academy.parameters.msg_invoice_saved'));
      setTick(t => t + 1);
    } catch (err) {
      blobticMessage.error(t('academy.parameters.saveTooltip'), t('academy.parameters.msg_invoice_error'));
    } finally {
      setSavingCode(null);
    }
  };

  const handleSavePaymentNumbering = async () => {
    setSavingCode('Payment_Numbering');
    try {
      await academyStore.saveAcademyParameter({ id: getParamId('Payment_Number_Prefix'),        dos_idf: connectedDosId, code: 'Payment_Number_Prefix',        value: payPrefix,             lib: 'Préfixe reçu',           type: 'S' });
      await academyStore.saveAcademyParameter({ id: getParamId('Payment_Number_Agency_Length'), dos_idf: connectedDosId, code: 'Payment_Number_Agency_Length', value: String(payAgencyLength), lib: 'Longueur agence reçu',    type: 'N' });
      await academyStore.saveAcademyParameter({ id: getParamId('Payment_Number_DateFormat'),    dos_idf: connectedDosId, code: 'Payment_Number_DateFormat',    value: payDateFormat,          lib: 'Format date reçu',       type: 'S' });
      await academyStore.saveAcademyParameter({ id: getParamId('Payment_Number_Padding'),       dos_idf: connectedDosId, code: 'Payment_Number_Padding',       value: String(payPadding),     lib: 'Longueur zéros reçu',   type: 'N' });
      await academyStore.saveAcademyParameter({ id: getParamId('Payment_Number_Suffix'),        dos_idf: connectedDosId, code: 'Payment_Number_Suffix',        value: paySuffix,             lib: 'Suffixe reçu',           type: 'S' });
      await academyStore.saveAcademyParameter({ id: getParamId('Payment_Number_Next_Counter'),  dos_idf: connectedDosId, code: 'Payment_Number_Next_Counter',  value: String(payCounter),     lib: 'Compteur reçu',          type: 'N' });
      await academyStore.saveAcademyParameter({ id: getParamId('Payment_Number_Reset_Period'),  dos_idf: connectedDosId, code: 'Payment_Number_Reset_Period',  value: payResetPeriod,        lib: 'Remise à zéro reçu',    type: 'S' });
      await academyStore.saveAcademyParameter({ id: getParamId('Payment_Number_Order'),         dos_idf: connectedDosId, code: 'Payment_Number_Order',         value: payOrder,               lib: 'Ordre éléments reçu',   type: 'S' });
      await academyStore.saveAcademyParameter({ id: getParamId('Payment_Number_Separator'),     dos_idf: connectedDosId, code: 'Payment_Number_Separator',     value: paySeparator,          lib: 'Séparateur reçu',        type: 'S' });

      blobticMessage.success(t('academy.parameters.saveTooltip'), t('academy.parameters.msg_payment_saved'));
      setTick(t => t + 1);
    } catch (err) {
      blobticMessage.error(t('academy.parameters.saveTooltip'), t('academy.parameters.msg_payment_error'));
    } finally {
      setSavingCode(null);
    }
  };

  const handleResetInvoiceCounter = async () => {
    // boubaker 23/08/2026 - confirmation via le composant générique animé
    if (!(await blobticMessage.confirm({
      title: t('academy.parameters.saveTooltip'),
      message: t('academy.parameters.numbering_reset_confirm'),
      severity: 'question',
      confirmText: t('common.actions.confirm'),
      cancelText: t('btn.cancel')
    }))) return;
    setSavingCode('Reset_Invoice');
    try {
      await academyStore.resetInvoiceCounter(1, connectedDosId);
      setInvCounter(1);
      blobticMessage.success(t('academy.parameters.saveTooltip'), t('academy.parameters.numbering_reset_success'));
      setTick(t => t + 1);
    } catch (err) {
      blobticMessage.error(t('academy.parameters.saveTooltip'), 'Erreur lors de la réinitialisation');
    } finally {
      setSavingCode(null);
    }
  };

  const handleResetPaymentCounter = async () => {
    if (!(await blobticMessage.confirm({
      title: t('academy.parameters.saveTooltip'),
      message: t('academy.parameters.numbering_reset_confirm'),
      severity: 'question',
      confirmText: t('common.actions.confirm'),
      cancelText: t('btn.cancel')
    }))) return;
    setSavingCode('Reset_Payment');
    try {
      await academyStore.resetPaymentCounter(1, connectedDosId);
      setPayCounter(1);
      blobticMessage.success(t('academy.parameters.saveTooltip'), t('academy.parameters.numbering_reset_success'));
      setTick(t => t + 1);
    } catch (err) {
      blobticMessage.error(t('academy.parameters.saveTooltip'), 'Erreur lors de la réinitialisation');
    } finally {
      setSavingCode(null);
    }
  };

  // Get current parameter entity DTOs for connected agency
  const currentParams = academyStore.getAcademyParameters(connectedDosId);
  const pTimer = currentParams.find(p => p.code === 'Dashboard_Refresh_Timer_Seconds');
  const pAutoApprove = currentParams.find(p => p.code === 'Auto_Approve_PreRegistrations');
  const pQuizScore = currentParams.find(p => p.code === 'Default_Quiz_Passing_Score');
  const pMaxStudents = currentParams.find(p => p.code === 'Max_Active_Students_Per_Class');
  const pShowUser = currentParams.find(p => p.code === 'Show_User_On_Editions');
  const pGroupDeleteCascade = currentParams.find(p => p.code === 'Delete_Group_Cascade_Sessions');
  const pPlanningStart = currentParams.find(p => p.code === 'Planning_Start_Time');
  const pInvoiceNum = currentParams.find(p => p.code === 'Invoice_Number_Prefix') || currentParams.find(p => p.code === 'Invoice_Number_Next_Counter');
  const pPaymentNum = currentParams.find(p => p.code === 'Payment_Number_Prefix') || currentParams.find(p => p.code === 'Payment_Number_Next_Counter');

  const agencyCode = (currentParams.length > 0 && currentParams[0].dos_code) ? currentParams[0].dos_code : 'ACADEMY';

  const invPreview = formatDocumentNumber(
    {
      prefix: invPrefix,
      agencyLength: invAgencyLength,
      dateFormat: invDateFormat,
      padding: invPadding,
      suffix: invSuffix,
      order: invOrder,
      separator: invSeparator
    },
    invCounter,
    new Date(),
    agencyCode
  );

  const payPreview = formatDocumentNumber(
    {
      prefix: payPrefix,
      agencyLength: payAgencyLength,
      dateFormat: payDateFormat,
      padding: payPadding,
      suffix: paySuffix,
      order: payOrder,
      separator: paySeparator
    },
    payCounter,
    new Date(),
    agencyCode
  );

  return (
    <GenericPage title={t('academy.parameters.pageTitle')} footerEnabled={false}>
      <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
        {loading && <LinearProgress sx={{ mb: 2 }} />}

        {/* Direct Cards Grid */}
        <Grid container spacing={3}>
          {/* Card: Invoice Numbering Configuration */}
          <Grid item xs={12} md={6}>
            <Card elevation={3} sx={{ height: '100%', borderRadius: 2, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <CardContent>
                <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <InvoiceIcon color="primary" />
                    <Typography variant="subtitle1" fontWeight={700}>
                      {t('academy.parameters.numbering_invoice_title')}
                    </Typography>
                  </Stack>

                  <Tooltip title={t('academy.parameters.saveTooltip')}>
                    <span>
                      <IconButton
                        color="primary"
                        size="medium"
                        onClick={handleSaveInvoiceNumbering}
                        disabled={savingCode === 'Invoice_Numbering'}
                        sx={{
                          bgcolor: 'primary.main',
                          color: 'white',
                          '&:hover': { bgcolor: 'primary.dark' },
                          '&.Mui-disabled': { bgcolor: 'action.disabledBackground', color: 'action.disabled' }
                        }}
                      >
                        {savingCode === 'Invoice_Numbering' ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          <SaveIcon fontSize="small" />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
                <Divider sx={{ mb: 2 }} />

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('academy.parameters.numbering_invoice_desc')}
                </Typography>

                {/* Live Preview Banner */}
                <Alert severity="info" icon={false} sx={{ mb: 2.5, borderRadius: 2, bgcolor: 'primary.50' }}>
                  <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                    <Typography variant="body2" fontWeight={600} color="primary.dark">
                      {t('academy.parameters.numbering_preview')}
                    </Typography>
                    <Chip
                      label={invPreview || '—'}
                      color="primary"
                      sx={{ fontWeight: 800, fontSize: '0.95rem', letterSpacing: 0.5, px: 1 }}
                    />
                  </Stack>
                </Alert>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t('academy.parameters.numbering_prefix')}
                      value={invPrefix}
                      onChange={(e) => setInvPrefix(e.target.value)}
                      placeholder="INV"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label={t('academy.parameters.numbering_agency_length')}
                      value={invAgencyLength}
                      onChange={(e) => setInvAgencyLength(Math.max(0, parseInt(e.target.value, 10) || 0))}
                      helperText={t('academy.parameters.numbering_agency_length_helper')}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      fullWidth
                      label={t('academy.parameters.numbering_date_format')}
                      value={invDateFormat}
                      onChange={(e) => setInvDateFormat(e.target.value)}
                    >
                      <MenuItem value="None">{t('academy.parameters.datefmt_none')}</MenuItem>
                      <MenuItem value="YYYY">{t('academy.parameters.datefmt_yyyy')}</MenuItem>
                      <MenuItem value="YYYY-MM">{t('academy.parameters.datefmt_yyyy_mm')}</MenuItem>
                      <MenuItem value="YYYY/MM">{t('academy.parameters.datefmt_yyyy_slash_mm')}</MenuItem>
                      <MenuItem value="YYYYMMDD">{t('academy.parameters.datefmt_yyyymmdd')}</MenuItem>
                      <MenuItem value="YYMM">{t('academy.parameters.datefmt_yymm')}</MenuItem>
                    </TextField>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label={t('academy.parameters.numbering_padding')}
                      value={invPadding}
                      onChange={(e) => setInvPadding(Math.max(1, parseInt(e.target.value, 10) || 1))}
                      helperText="Ex: 4 -> 0001"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t('academy.parameters.numbering_suffix')}
                      value={invSuffix}
                      onChange={(e) => setInvSuffix(e.target.value)}
                      placeholder="ACAD"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      fullWidth
                      label={t('academy.parameters.numbering_separator')}
                      value={invSeparator}
                      onChange={(e) => setInvSeparator(e.target.value)}
                    >
                      <MenuItem value="-">{t('academy.parameters.sep_dash')}</MenuItem>
                      <MenuItem value="/">{t('academy.parameters.sep_slash')}</MenuItem>
                      <MenuItem value=".">{t('academy.parameters.sep_dot')}</MenuItem>
                      <MenuItem value="_">{t('academy.parameters.sep_underscore')}</MenuItem>
                      <MenuItem value="None">{t('academy.parameters.sep_none')}</MenuItem>
                    </TextField>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      select
                      fullWidth
                      label={t('academy.parameters.numbering_order')}
                      value={invOrder}
                      onChange={(e) => setInvOrder(e.target.value)}
                    >
                      <MenuItem value="PREFIX,AGENCY,DATE,NUMBER,SUFFIX">{t('academy.parameters.order_prefix_agency_date_number_suffix')}</MenuItem>
                      <MenuItem value="PREFIX,DATE,NUMBER,SUFFIX">{t('academy.parameters.order_prefix_date_number_suffix')}</MenuItem>
                      <MenuItem value="PREFIX,AGENCY,NUMBER,DATE">{t('academy.parameters.order_prefix_agency_number_date')}</MenuItem>
                      <MenuItem value="PREFIX,NUMBER,DATE">{t('academy.parameters.order_prefix_number_date')}</MenuItem>
                      <MenuItem value="DATE,PREFIX,NUMBER">{t('academy.parameters.order_date_prefix_number')}</MenuItem>
                      <MenuItem value="PREFIX,NUMBER,SUFFIX">{t('academy.parameters.order_prefix_number_suffix')}</MenuItem>
                    </TextField>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      fullWidth
                      label={t('academy.parameters.numbering_reset_period')}
                      value={invResetPeriod}
                      onChange={(e) => setInvResetPeriod(e.target.value)}
                    >
                      <MenuItem value="Yearly">{t('academy.parameters.reset_yearly')}</MenuItem>
                      <MenuItem value="Monthly">{t('academy.parameters.reset_monthly')}</MenuItem>
                      <MenuItem value="Daily">{t('academy.parameters.reset_daily')}</MenuItem>
                      <MenuItem value="Manual">{t('academy.parameters.reset_manual')}</MenuItem>
                      <MenuItem value="Never">{t('academy.parameters.reset_never')}</MenuItem>
                    </TextField>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TextField
                        fullWidth
                        type="number"
                        label={t('academy.parameters.numbering_next_counter')}
                        value={invCounter}
                        onChange={(e) => setInvCounter(Math.max(1, parseInt(e.target.value, 10) || 1))}
                      />
                      <Tooltip title={t('academy.parameters.numbering_reset_btn')}>
                        <IconButton
                          color="warning"
                          onClick={handleResetInvoiceCounter}
                          disabled={savingCode === 'Reset_Invoice'}
                          sx={{ border: '1px solid', borderColor: 'warning.main', borderRadius: 1.5, p: 1 }}
                        >
                          <ResetIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Grid>
                </Grid>
              </CardContent>

              {/* Audit Metadata Footer */}
              <Box sx={{ p: 1.5, px: 2, borderTop: '1px dashed', borderColor: 'divider', bgcolor: 'action.hover' }}>
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={6}>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <PersonIcon fontSize="inherit" color="action" />
                      <Typography variant="caption" color="text.secondary">
                        {t('academy.parameters.modifiedBy')} <strong>{pInvoiceNum?.user_lib || t('academy.parameters.systemUser')}</strong>
                      </Typography>
                    </Stack>
                  </Grid>
                  <Grid item xs={6} sx={{ textAlign: 'right' }}>
                    <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
                      <AccessTimeIcon fontSize="inherit" color="action" />
                      <Typography variant="caption" color="text.secondary">
                        {t('academy.parameters.lastUpdate')} <strong>{pInvoiceNum?.last_updt || '—'}</strong>
                      </Typography>
                    </Stack>
                  </Grid>
                </Grid>
              </Box>
            </Card>
          </Grid>

          {/* Card: Payment Receipt Numbering Configuration */}
          <Grid item xs={12} md={6}>
            <Card elevation={3} sx={{ height: '100%', borderRadius: 2, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <CardContent>
                <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <ReceiptLongIcon color="primary" />
                    <Typography variant="subtitle1" fontWeight={700}>
                      {t('academy.parameters.numbering_payment_title')}
                    </Typography>
                  </Stack>

                  <Tooltip title={t('academy.parameters.saveTooltip')}>
                    <span>
                      <IconButton
                        color="primary"
                        size="medium"
                        onClick={handleSavePaymentNumbering}
                        disabled={savingCode === 'Payment_Numbering'}
                        sx={{
                          bgcolor: 'primary.main',
                          color: 'white',
                          '&:hover': { bgcolor: 'primary.dark' },
                          '&.Mui-disabled': { bgcolor: 'action.disabledBackground', color: 'action.disabled' }
                        }}
                      >
                        {savingCode === 'Payment_Numbering' ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          <SaveIcon fontSize="small" />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
                <Divider sx={{ mb: 2 }} />

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('academy.parameters.numbering_payment_desc')}
                </Typography>

                {/* Live Preview Banner */}
                <Alert severity="info" icon={false} sx={{ mb: 2.5, borderRadius: 2, bgcolor: 'primary.50' }}>
                  <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                    <Typography variant="body2" fontWeight={600} color="primary.dark">
                      {t('academy.parameters.numbering_preview')}
                    </Typography>
                    <Chip
                      label={payPreview || '—'}
                      color="primary"
                      sx={{ fontWeight: 800, fontSize: '0.95rem', letterSpacing: 0.5, px: 1 }}
                    />
                  </Stack>
                </Alert>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t('academy.parameters.numbering_prefix')}
                      value={payPrefix}
                      onChange={(e) => setPayPrefix(e.target.value)}
                      placeholder="REC"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label={t('academy.parameters.numbering_agency_length')}
                      value={payAgencyLength}
                      onChange={(e) => setPayAgencyLength(Math.max(0, parseInt(e.target.value, 10) || 0))}
                      helperText={t('academy.parameters.numbering_agency_length_helper')}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      fullWidth
                      label={t('academy.parameters.numbering_date_format')}
                      value={payDateFormat}
                      onChange={(e) => setPayDateFormat(e.target.value)}
                    >
                      <MenuItem value="None">{t('academy.parameters.datefmt_none')}</MenuItem>
                      <MenuItem value="YYYY">{t('academy.parameters.datefmt_yyyy')}</MenuItem>
                      <MenuItem value="YYYY-MM">{t('academy.parameters.datefmt_yyyy_mm')}</MenuItem>
                      <MenuItem value="YYYY/MM">{t('academy.parameters.datefmt_yyyy_slash_mm')}</MenuItem>
                      <MenuItem value="YYYYMMDD">{t('academy.parameters.datefmt_yyyymmdd')}</MenuItem>
                      <MenuItem value="YYMM">{t('academy.parameters.datefmt_yymm')}</MenuItem>
                    </TextField>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label={t('academy.parameters.numbering_padding')}
                      value={payPadding}
                      onChange={(e) => setPayPadding(Math.max(1, parseInt(e.target.value, 10) || 1))}
                      helperText="Ex: 4 -> 0001"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t('academy.parameters.numbering_suffix')}
                      value={paySuffix}
                      onChange={(e) => setPaySuffix(e.target.value)}
                      placeholder="REC"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      fullWidth
                      label={t('academy.parameters.numbering_separator')}
                      value={paySeparator}
                      onChange={(e) => setPaySeparator(e.target.value)}
                    >
                      <MenuItem value="-">{t('academy.parameters.sep_dash')}</MenuItem>
                      <MenuItem value="/">{t('academy.parameters.sep_slash')}</MenuItem>
                      <MenuItem value=".">{t('academy.parameters.sep_dot')}</MenuItem>
                      <MenuItem value="_">{t('academy.parameters.sep_underscore')}</MenuItem>
                      <MenuItem value="None">{t('academy.parameters.sep_none')}</MenuItem>
                    </TextField>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      select
                      fullWidth
                      label={t('academy.parameters.numbering_order')}
                      value={payOrder}
                      onChange={(e) => setPayOrder(e.target.value)}
                    >
                      <MenuItem value="PREFIX,NUMBER,SUFFIX">{t('academy.parameters.order_prefix_number_suffix')}</MenuItem>
                      <MenuItem value="PREFIX,AGENCY,NUMBER,SUFFIX">{t('academy.parameters.order_prefix_agency_number_suffix')}</MenuItem>
                      <MenuItem value="PREFIX,AGENCY,DATE,NUMBER,SUFFIX">{t('academy.parameters.order_prefix_agency_date_number_suffix')}</MenuItem>
                      <MenuItem value="PREFIX,DATE,NUMBER,SUFFIX">{t('academy.parameters.order_prefix_date_number_suffix')}</MenuItem>
                      <MenuItem value="PREFIX,NUMBER,DATE">{t('academy.parameters.order_prefix_number_date')}</MenuItem>
                      <MenuItem value="DATE,PREFIX,NUMBER">{t('academy.parameters.order_date_prefix_number')}</MenuItem>
                    </TextField>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      fullWidth
                      label={t('academy.parameters.numbering_reset_period')}
                      value={payResetPeriod}
                      onChange={(e) => setPayResetPeriod(e.target.value)}
                    >
                      <MenuItem value="Never">{t('academy.parameters.reset_never')}</MenuItem>
                      <MenuItem value="Yearly">{t('academy.parameters.reset_yearly')}</MenuItem>
                      <MenuItem value="Monthly">{t('academy.parameters.reset_monthly')}</MenuItem>
                      <MenuItem value="Daily">{t('academy.parameters.reset_daily')}</MenuItem>
                      <MenuItem value="Manual">{t('academy.parameters.reset_manual')}</MenuItem>
                    </TextField>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TextField
                        fullWidth
                        type="number"
                        label={t('academy.parameters.numbering_next_counter')}
                        value={payCounter}
                        onChange={(e) => setPayCounter(Math.max(1, parseInt(e.target.value, 10) || 1))}
                      />
                      <Tooltip title={t('academy.parameters.numbering_reset_btn')}>
                        <IconButton
                          color="warning"
                          onClick={handleResetPaymentCounter}
                          disabled={savingCode === 'Reset_Payment'}
                          sx={{ border: '1px solid', borderColor: 'warning.main', borderRadius: 1.5, p: 1 }}
                        >
                          <ResetIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Grid>
                </Grid>
              </CardContent>

              {/* Audit Metadata Footer */}
              <Box sx={{ p: 1.5, px: 2, borderTop: '1px dashed', borderColor: 'divider', bgcolor: 'action.hover' }}>
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={6}>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <PersonIcon fontSize="inherit" color="action" />
                      <Typography variant="caption" color="text.secondary">
                        {t('academy.parameters.modifiedBy')} <strong>{pPaymentNum?.user_lib || t('academy.parameters.systemUser')}</strong>
                      </Typography>
                    </Stack>
                  </Grid>
                  <Grid item xs={6} sx={{ textAlign: 'right' }}>
                    <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
                      <AccessTimeIcon fontSize="inherit" color="action" />
                      <Typography variant="caption" color="text.secondary">
                        {t('academy.parameters.lastUpdate')} <strong>{pPaymentNum?.last_updt || '—'}</strong>
                      </Typography>
                    </Stack>
                  </Grid>
                </Grid>
              </Box>
            </Card>
          </Grid>
          {/* Card 1: Dashboard Refresh Timer */}
          <Grid item xs={12} md={6}>
            <Card elevation={3} sx={{ height: '100%', borderRadius: 2, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <CardContent>
                <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <TimerIcon color="primary" />
                    <Typography variant="subtitle1" fontWeight={700}>
                      {t('academy.parameters.timerTitle')}
                    </Typography>
                  </Stack>

                  {/* Icon Only Save Button */}
                  <Tooltip title={t('academy.parameters.saveTooltip')}>
                    <span>
                      <IconButton
                        color="primary"
                        size="medium"
                        onClick={() => handleSaveSingleParameter(
                          'Dashboard_Refresh_Timer_Seconds',
                          String(timerSeconds),
                          'Durée de rafraîchissement du tableau de bord de l\'agence en secondes (0 = désactivé)',
                          'N'
                        )}
                        disabled={savingCode === 'Dashboard_Refresh_Timer_Seconds'}
                        sx={{
                          bgcolor: 'primary.main',
                          color: 'white',
                          '&:hover': { bgcolor: 'primary.dark' },
                          '&.Mui-disabled': { bgcolor: 'action.disabledBackground', color: 'action.disabled' }
                        }}
                      >
                        {savingCode === 'Dashboard_Refresh_Timer_Seconds' ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          <SaveIcon fontSize="small" />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
                <Divider sx={{ mb: 2 }} />

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('academy.parameters.timerDesc')}
                </Typography>

                <TextField
                  fullWidth
                  label={t('academy.parameters.timerLabel')}
                  type="number"
                  value={timerSeconds}
                  onChange={(e) => setTimerSeconds(Math.max(0, parseInt(e.target.value, 10) || 0))}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">{t('academy.parameters.timerUnit')}</InputAdornment>,
                  }}
                  helperText={
                    timerSeconds === 0
                      ? t('academy.parameters.timerDisabledHelper')
                      : t('academy.parameters.timerActiveHelper', timerSeconds)
                  }
                  sx={{ mb: 2 }}
                />

                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  {t('academy.parameters.quickPresets')}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  {[0, 30, 60, 120, 300].map((sec) => (
                    <Chip
                      key={sec}
                      label={sec === 0 ? t('academy.parameters.chipDisabled') : `${sec}s`}
                      color={timerSeconds === sec ? 'primary' : 'default'}
                      clickable
                      onClick={() => setTimerSeconds(sec)}
                      size="small"
                    />
                  ))}
                </Stack>
              </CardContent>

              {/* Audit Metadata Footer */}
              <Box sx={{ p: 1.5, px: 2, borderTop: '1px dashed', borderColor: 'divider', bgcolor: 'action.hover' }}>
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={6}>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <PersonIcon fontSize="inherit" color="action" />
                      <Typography variant="caption" color="text.secondary">
                        {t('academy.parameters.modifiedBy')} <strong>{pTimer?.user_lib || t('academy.parameters.systemUser')}</strong>
                      </Typography>
                    </Stack>
                  </Grid>
                  <Grid item xs={6} sx={{ textAlign: 'right' }}>
                    <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
                      <AccessTimeIcon fontSize="inherit" color="action" />
                      <Typography variant="caption" color="text.secondary">
                        {t('academy.parameters.lastUpdate')} <strong>{pTimer?.last_updt || '—'}</strong>
                      </Typography>
                    </Stack>
                  </Grid>
                </Grid>
              </Box>
            </Card>
          </Grid>

          {/* Card 2: Pre-registration Rules */}
          <Grid item xs={12} md={6}>
            <Card elevation={3} sx={{ height: '100%', borderRadius: 2, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <CardContent>
                <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <FactCheckIcon color="primary" />
                    <Typography variant="subtitle1" fontWeight={700}>
                      {t('academy.parameters.autoApproveTitle')}
                    </Typography>
                  </Stack>

                  {/* Icon Only Save Button */}
                  <Tooltip title={t('academy.parameters.saveTooltip')}>
                    <span>
                      <IconButton
                        color="primary"
                        size="medium"
                        onClick={() => handleSaveSingleParameter(
                          'Auto_Approve_PreRegistrations',
                          autoApprove ? '1' : '0',
                          'Validation automatique des pré-inscriptions des étudiants (0 = Non, 1 = Oui)',
                          'N'
                        )}
                        disabled={savingCode === 'Auto_Approve_PreRegistrations'}
                        sx={{
                          bgcolor: 'primary.main',
                          color: 'white',
                          '&:hover': { bgcolor: 'primary.dark' },
                          '&.Mui-disabled': { bgcolor: 'action.disabledBackground', color: 'action.disabled' }
                        }}
                      >
                        {savingCode === 'Auto_Approve_PreRegistrations' ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          <SaveIcon fontSize="small" />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
                <Divider sx={{ mb: 2 }} />

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('academy.parameters.autoApproveDesc')}
                </Typography>

                <FormControlLabel
                  control={
                    <Switch
                      checked={autoApprove}
                      onChange={(e) => setAutoApprove(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2" fontWeight={600}>
                      {autoApprove
                        ? t('academy.parameters.autoApproveEnabled')
                        : t('academy.parameters.autoApproveDisabled')}
                    </Typography>
                  }
                />

                <Alert severity={autoApprove ? "info" : "success"} sx={{ mt: 2 }}>
                  {autoApprove
                    ? t('academy.parameters.autoApproveAlertTrue')
                    : t('academy.parameters.autoApproveAlertFalse')}
                </Alert>
              </CardContent>

              {/* Audit Metadata Footer */}
              <Box sx={{ p: 1.5, px: 2, borderTop: '1px dashed', borderColor: 'divider', bgcolor: 'action.hover' }}>
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={6}>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <PersonIcon fontSize="inherit" color="action" />
                      <Typography variant="caption" color="text.secondary">
                        {t('academy.parameters.modifiedBy')} <strong>{pAutoApprove?.user_lib || t('academy.parameters.systemUser')}</strong>
                      </Typography>
                    </Stack>
                  </Grid>
                  <Grid item xs={6} sx={{ textAlign: 'right' }}>
                    <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
                      <AccessTimeIcon fontSize="inherit" color="action" />
                      <Typography variant="caption" color="text.secondary">
                        {t('academy.parameters.lastUpdate')} <strong>{pAutoApprove?.last_updt || '—'}</strong>
                      </Typography>
                    </Stack>
                  </Grid>
                </Grid>
              </Box>
            </Card>
          </Grid>

          {/* Card 3: Quiz Passing Score */}
          <Grid item xs={12} md={6}>
            <Card elevation={3} sx={{ height: '100%', borderRadius: 2, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <CardContent>
                <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <QuizIcon color="primary" />
                    <Typography variant="subtitle1" fontWeight={700}>
                      {t('academy.parameters.quizScoreTitle')}
                    </Typography>
                  </Stack>

                  {/* Icon Only Save Button */}
                  <Tooltip title={t('academy.parameters.saveTooltip')}>
                    <span>
                      <IconButton
                        color="primary"
                        size="medium"
                        onClick={() => handleSaveSingleParameter(
                          'Default_Quiz_Passing_Score',
                          String(quizMinScore),
                          'Score minimal de réussite par défaut des quiz (%)',
                          'N'
                        )}
                        disabled={savingCode === 'Default_Quiz_Passing_Score'}
                        sx={{
                          bgcolor: 'primary.main',
                          color: 'white',
                          '&:hover': { bgcolor: 'primary.dark' },
                          '&.Mui-disabled': { bgcolor: 'action.disabledBackground', color: 'action.disabled' }
                        }}
                      >
                        {savingCode === 'Default_Quiz_Passing_Score' ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          <SaveIcon fontSize="small" />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
                <Divider sx={{ mb: 2 }} />

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('academy.parameters.quizScoreDesc')}
                </Typography>

                <TextField
                  fullWidth
                  label={t('academy.parameters.quizScoreLabel')}
                  type="number"
                  value={quizMinScore}
                  onChange={(e) => setQuizMinScore(Math.min(100, Math.max(0, parseInt(e.target.value, 10) || 0)))}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                  helperText={t('academy.parameters.quizScoreHelper', quizMinScore)}
                />
              </CardContent>

              {/* Audit Metadata Footer */}
              <Box sx={{ p: 1.5, px: 2, borderTop: '1px dashed', borderColor: 'divider', bgcolor: 'action.hover' }}>
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={6}>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <PersonIcon fontSize="inherit" color="action" />
                      <Typography variant="caption" color="text.secondary">
                        {t('academy.parameters.modifiedBy')} <strong>{pQuizScore?.user_lib || t('academy.parameters.systemUser')}</strong>
                      </Typography>
                    </Stack>
                  </Grid>
                  <Grid item xs={6} sx={{ textAlign: 'right' }}>
                    <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
                      <AccessTimeIcon fontSize="inherit" color="action" />
                      <Typography variant="caption" color="text.secondary">
                        {t('academy.parameters.lastUpdate')} <strong>{pQuizScore?.last_updt || '—'}</strong>
                      </Typography>
                    </Stack>
                  </Grid>
                </Grid>
              </Box>
            </Card>
          </Grid>

          {/* Card 4: Class Capacity Limit */}
          <Grid item xs={12} md={6}>
            <Card elevation={3} sx={{ height: '100%', borderRadius: 2, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <CardContent>
                <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <PeopleIcon color="primary" />
                    <Typography variant="subtitle1" fontWeight={700}>
                      {t('academy.parameters.maxStudentsTitle')}
                    </Typography>
                  </Stack>

                  {/* Icon Only Save Button */}
                  <Tooltip title={t('academy.parameters.saveTooltip')}>
                    <span>
                      <IconButton
                        color="primary"
                        size="medium"
                        onClick={() => handleSaveSingleParameter(
                          'Max_Active_Students_Per_Class',
                          String(maxClassStudents),
                          'Nombre maximal d\'étudiants actifs par classe/groupe',
                          'N'
                        )}
                        disabled={savingCode === 'Max_Active_Students_Per_Class'}
                        sx={{
                          bgcolor: 'primary.main',
                          color: 'white',
                          '&:hover': { bgcolor: 'primary.dark' },
                          '&.Mui-disabled': { bgcolor: 'action.disabledBackground', color: 'action.disabled' }
                        }}
                      >
                        {savingCode === 'Max_Active_Students_Per_Class' ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          <SaveIcon fontSize="small" />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
                <Divider sx={{ mb: 2 }} />

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('academy.parameters.maxStudentsDesc')}
                </Typography>

                <TextField
                  fullWidth
                  label={t('academy.parameters.maxStudentsLabel')}
                  type="number"
                  value={maxClassStudents}
                  onChange={(e) => setMaxClassStudents(Math.max(1, parseInt(e.target.value, 10) || 1))}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">{t('academy.parameters.maxStudentsUnit')}</InputAdornment>,
                  }}
                  helperText={t('academy.parameters.maxStudentsHelper', maxClassStudents)}
                />
              </CardContent>

              {/* Audit Metadata Footer */}
              <Box sx={{ p: 1.5, px: 2, borderTop: '1px dashed', borderColor: 'divider', bgcolor: 'action.hover' }}>
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={6}>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <PersonIcon fontSize="inherit" color="action" />
                      <Typography variant="caption" color="text.secondary">
                        {t('academy.parameters.modifiedBy')} <strong>{pMaxStudents?.user_lib || t('academy.parameters.systemUser')}</strong>
                      </Typography>
                    </Stack>
                  </Grid>
                  <Grid item xs={6} sx={{ textAlign: 'right' }}>
                    <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
                      <AccessTimeIcon fontSize="inherit" color="action" />
                      <Typography variant="caption" color="text.secondary">
                        {t('academy.parameters.lastUpdate')} <strong>{pMaxStudents?.last_updt || '—'}</strong>
                      </Typography>
                    </Stack>
                  </Grid>
                </Grid>
              </Box>
            </Card>
          </Grid>

          {/* Card: Display Connected User on Printed Document Editions */}
          <Grid item xs={12} md={6}>
            <Card elevation={3} sx={{ height: '100%', borderRadius: 2, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <CardContent>
                <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <AssignmentIndIcon color="primary" />
                    <Typography variant="subtitle1" fontWeight={700}>
                      {t('academy.parameters.showUserEditionsTitle')}
                    </Typography>
                  </Stack>

                  <Tooltip title={t('academy.parameters.saveTooltip')}>
                    <span>
                      <IconButton
                        color="primary"
                        size="medium"
                        onClick={() => handleSaveSingleParameter(
                          'Show_User_On_Editions',
                          showUserOnEditions ? '1' : '0',
                          'Afficher l\'utilisateur connecté au bas des éditions (0 = Non, 1 = Oui)',
                          'N'
                        )}
                        disabled={savingCode === 'Show_User_On_Editions'}
                        sx={{
                          bgcolor: 'primary.main',
                          color: 'white',
                          '&:hover': { bgcolor: 'primary.dark' },
                          '&.Mui-disabled': { bgcolor: 'action.disabledBackground', color: 'action.disabled' }
                        }}
                      >
                        {savingCode === 'Show_User_On_Editions' ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          <SaveIcon fontSize="small" />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
                <Divider sx={{ mb: 2 }} />

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('academy.parameters.showUserEditionsDesc')}
                </Typography>

                <FormControlLabel
                  control={
                    <Switch
                      checked={showUserOnEditions}
                      onChange={(e) => setShowUserOnEditions(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2" fontWeight={600}>
                      {showUserOnEditions
                        ? t('academy.parameters.showUserEditionsEnabled')
                        : t('academy.parameters.showUserEditionsDisabled')}
                    </Typography>
                  }
                  sx={{ mb: 1 }}
                />

                <Alert severity={showUserOnEditions ? 'info' : 'warning'} sx={{ mt: 1 }}>
                  {showUserOnEditions
                    ? t('academy.parameters.showUserEditionsEnabled')
                    : t('academy.parameters.showUserEditionsDisabled')}
                </Alert>
              </CardContent>

              {/* Audit Metadata Footer */}
              <Box sx={{ p: 1.5, px: 2, borderTop: '1px dashed', borderColor: 'divider', bgcolor: 'action.hover' }}>
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={6}>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <PersonIcon fontSize="inherit" color="action" />
                      <Typography variant="caption" color="text.secondary">
                        {t('academy.parameters.modifiedBy')} <strong>{pShowUser?.user_lib || t('academy.parameters.systemUser')}</strong>
                      </Typography>
                    </Stack>
                  </Grid>
                  <Grid item xs={6} sx={{ textAlign: 'right' }}>
                    <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
                      <AccessTimeIcon fontSize="inherit" color="action" />
                      <Typography variant="caption" color="text.secondary">
                        {t('academy.parameters.lastUpdate')} <strong>{pShowUser?.last_updt || '—'}</strong>
                      </Typography>
                    </Stack>
                  </Grid>
                </Grid>
              </Box>
            </Card>
          </Grid>

          {/* Card: Group & Scheduled Sessions Deletion Control */}
          <Grid item xs={12} md={6}>
            <Card elevation={3} sx={{ height: '100%', borderRadius: 2, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <CardContent>
                <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <DeleteForeverIcon color="error" />
                    <Typography variant="subtitle1" fontWeight={700}>
                      {t('academy.parameters.groupDeleteCascadeTitle')}
                    </Typography>
                  </Stack>

                  <Tooltip title={t('academy.parameters.saveTooltip')}>
                    <span>
                      <IconButton
                        color="primary"
                        size="medium"
                        onClick={() => handleSaveSingleParameter(
                          'Delete_Group_Cascade_Sessions',
                          deleteGroupCascadeSessions ? '1' : '0',
                          'Suppression automatique des créneaux planifiés lors de la suppression d\'un groupe (0 = Bloquer, 1 = Cascade)',
                          'N'
                        )}
                        disabled={savingCode === 'Delete_Group_Cascade_Sessions'}
                        sx={{
                          bgcolor: 'primary.main',
                          color: 'white',
                          '&:hover': { bgcolor: 'primary.dark' },
                          '&.Mui-disabled': { bgcolor: 'action.disabledBackground', color: 'action.disabled' }
                        }}
                      >
                        {savingCode === 'Delete_Group_Cascade_Sessions' ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          <SaveIcon fontSize="small" />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
                <Divider sx={{ mb: 2 }} />

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('academy.parameters.groupDeleteCascadeDesc')}
                </Typography>

                <FormControlLabel
                  control={
                    <Switch
                      checked={deleteGroupCascadeSessions}
                      onChange={(e) => setDeleteGroupCascadeSessions(e.target.checked)}
                      color="error"
                    />
                  }
                  label={
                    <Typography variant="body2" fontWeight={600}>
                      {deleteGroupCascadeSessions
                        ? t('academy.parameters.groupDeleteCascadeEnabled')
                        : t('academy.parameters.groupDeleteCascadeDisabled')}
                    </Typography>
                  }
                  sx={{ mb: 1 }}
                />

                <Alert severity={deleteGroupCascadeSessions ? 'info' : 'warning'} sx={{ mt: 1 }}>
                  {deleteGroupCascadeSessions
                    ? t('academy.parameters.groupDeleteCascadeAlertTrue')
                    : t('academy.parameters.groupDeleteCascadeAlertFalse')}
                </Alert>
              </CardContent>

              {/* Audit Metadata Footer */}
              <Box sx={{ p: 1.5, px: 2, borderTop: '1px dashed', borderColor: 'divider', bgcolor: 'action.hover' }}>
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={6}>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <PersonIcon fontSize="inherit" color="action" />
                      <Typography variant="caption" color="text.secondary">
                        {t('academy.parameters.modifiedBy')} <strong>{pGroupDeleteCascade?.user_lib || t('academy.parameters.systemUser')}</strong>
                      </Typography>
                    </Stack>
                  </Grid>
                  <Grid item xs={6} sx={{ textAlign: 'right' }}>
                    <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
                      <AccessTimeIcon fontSize="inherit" color="action" />
                      <Typography variant="caption" color="text.secondary">
                        {t('academy.parameters.lastUpdate')} <strong>{pGroupDeleteCascade?.last_updt || '—'}</strong>
                      </Typography>
                    </Stack>
                  </Grid>
                </Grid>
              </Box>
            </Card>
          </Grid>

          {/* Card 5: Planning & Scheduler Configuration */}
          <Grid item xs={12}>
            <Card elevation={3} sx={{ borderRadius: 2 }}>
              <CardContent>
                <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <AccessTimeIcon color="primary" />
                    <Typography variant="subtitle1" fontWeight={700}>
                      {t('academy.parameters.matrix_title')}
                    </Typography>
                  </Stack>

                  <Tooltip title={t('academy.parameters.saveTooltip')}>
                    <span>
                      <IconButton
                        color="primary"
                        size="medium"
                        onClick={async () => {
                          await handleSaveSingleParameter('Planning_Start_Time', planningStartTime, 'Heure de début de journée du planning (HH:mm)', 'S');
                          await handleSaveSingleParameter('Planning_End_Time', planningEndTime, 'Heure de fin de journée du planning (HH:mm)', 'S');
                          await handleSaveSingleParameter('Planning_Slot_Duration_Minutes', String(planningSlotDuration), 'Durée de chaque créneau horaire en minutes', 'N');
                          await handleSaveSingleParameter('Planning_First_Day_Of_Week', String(planningFirstDayOfWeek), 'Premier jour de la semaine (0=Dimanche, 1=Lundi, 6=Samedi)', 'N');
                          await handleSaveSingleParameter('Planning_Default_View', planningDefaultView, 'Mode d\'affichage par défaut (day, week, month)', 'S');
                          await handleSaveSingleParameter('Planning_Weekend_Days', planningWeekendDays.join(','), 'Jours du week-end (0=Dimanche, 5=Vendredi, 6=Samedi)', 'S');
                          await handleSaveSingleParameter('Planning_Weekend_Bg_Color', planningWeekendBgColor, 'Couleur de fond du week-end dans le planning', 'S');
                        }}
                        disabled={savingCode !== null}
                        sx={{
                          bgcolor: 'primary.main',
                          color: 'white',
                          '&:hover': { bgcolor: 'primary.dark' },
                          '&.Mui-disabled': { bgcolor: 'action.disabledBackground', color: 'action.disabled' }
                        }}
                      >
                        {savingCode !== null ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          <SaveIcon fontSize="small" />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
                <Divider sx={{ mb: 2 }} />

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('academy.parameters.matrix_desc')}
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      label={t('academy.parameters.start_time')}
                      type="time"
                      value={planningStartTime}
                      onChange={(e) => setPlanningStartTime(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      label={t('academy.parameters.end_time')}
                      type="time"
                      value={planningEndTime}
                      onChange={(e) => setPlanningEndTime(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      label={t('academy.parameters.slot_duration')}
                      type="number"
                      value={planningSlotDuration}
                      onChange={(e) => setPlanningSlotDuration(Math.max(15, parseInt(e.target.value, 10) || 90))}
                      helperText="Ex: 90 min = 1h30"
                    />
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <TextField
                      select
                      fullWidth
                      label={t('academy.parameters.first_day')}
                      value={planningFirstDayOfWeek}
                      onChange={(e) => setPlanningFirstDayOfWeek(Number(e.target.value))}
                    >
                      <MenuItem value={0}>Dimanche</MenuItem>
                      <MenuItem value={1}>Lundi</MenuItem>
                      <MenuItem value={6}>Samedi</MenuItem>
                    </TextField>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      select
                      fullWidth
                      label={t('academy.parameters.default_view')}
                      value={planningDefaultView}
                      onChange={(e) => setPlanningDefaultView(e.target.value as 'day' | 'week' | 'month')}
                    >
                      <MenuItem value="day">{t('academy.scheduler.view_day')}</MenuItem>
                      <MenuItem value="week">{t('academy.scheduler.view_week')}</MenuItem>
                      <MenuItem value="month">{t('academy.scheduler.view_month')}</MenuItem>
                    </TextField>
                  </Grid>

                  {/* Weekend Days Selection */}
                  <Grid item xs={12} sm={4}>
                    <TextField
                      select
                      fullWidth
                      label={t('academy.parameters.weekend_days')}
                      value={planningWeekendDays.sort().join(',')}
                      onChange={(e) => {
                        const val = e.target.value;
                        setPlanningWeekendDays(val ? val.split(',').map(Number) : [5, 6]);
                      }}
                    >
                      <MenuItem value="5,6">Vendredi & Samedi (MENA / Algérie)</MenuItem>
                      <MenuItem value="6,0">Samedi & Dimanche (Europe / International)</MenuItem>
                      <MenuItem value="0">Dimanche Uniquement</MenuItem>
                      <MenuItem value="5">Vendredi Uniquement</MenuItem>
                      <MenuItem value="6">Samedi Uniquement</MenuItem>
                    </TextField>
                  </Grid>

                  {/* Weekend Background Color Libre Selection */}
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label={t('academy.parameters.weekend_color')}
                      value={planningWeekendBgColor}
                      onChange={(e) => setPlanningWeekendBgColor(e.target.value)}
                      placeholder="#fff7ed"
                      InputProps={{
                        startAdornment: (
                          <Box
                            component="input"
                            type="color"
                            value={planningWeekendBgColor.startsWith('#') ? planningWeekendBgColor : '#fff7ed'}
                            onChange={(e: any) => setPlanningWeekendBgColor(e.target.value)}
                            sx={{
                              width: 28,
                              height: 28,
                              border: 'none',
                              borderRadius: 1,
                              cursor: 'pointer',
                              mr: 1,
                              p: 0,
                              bgcolor: 'transparent'
                            }}
                          />
                        )
                      }}
                      helperText="Valeur hexadécimale libre (ex: #fff7ed, #ffedd5)"
                    />
                  </Grid>
                </Grid>
              </CardContent>

              {/* Audit Metadata Footer */}
              <Box sx={{ p: 1.5, px: 2, borderTop: '1px dashed', borderColor: 'divider', bgcolor: 'action.hover' }}>
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={6}>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <PersonIcon fontSize="inherit" color="action" />
                      <Typography variant="caption" color="text.secondary">
                        {t('academy.parameters.modifiedBy')} <strong>{pPlanningStart?.user_lib || t('academy.parameters.systemUser')}</strong>
                      </Typography>
                    </Stack>
                  </Grid>
                  <Grid item xs={6} sx={{ textAlign: 'right' }}>
                    <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
                      <AccessTimeIcon fontSize="inherit" color="action" />
                      <Typography variant="caption" color="text.secondary">
                        {t('academy.parameters.lastUpdate')} <strong>{pPlanningStart?.last_updt || '—'}</strong>
                      </Typography>
                    </Stack>
                  </Grid>
                </Grid>
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </GenericPage>
  );
};

export default AcademyParametersPage;
